import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { message } from "../../Reducers/loading/actions";
import "./wysiwyg.css";

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="tiptap-toolbar">
      <select
        className="tiptap-select"
        onChange={(e) => {
          const value = e.target.value;
          if (value === "p") {
            editor.chain().focus().setParagraph().run();
          } else {
            editor.chain().focus().toggleHeading({ level: parseInt(value) }).run();
          }
        }}
        value={
          editor.isActive("heading", { level: 1 }) ? "1" :
          editor.isActive("heading", { level: 2 }) ? "2" :
          editor.isActive("heading", { level: 3 }) ? "3" : "p"
        }
      >
        <option value="p">Normal</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
      </select>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive("bold") ? "is-active" : ""}
        title="Bold"
      >
        <b>B</b>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive("italic") ? "is-active" : ""}
        title="Italic"
      >
        <i>I</i>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={editor.isActive("strike") ? "is-active" : ""}
        title="Strikethrough"
      >
        <s>S</s>
      </button>
      <span className="tiptap-divider" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive("bulletList") ? "is-active" : ""}
        title="Bullet List"
      >
        &bull;
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive("orderedList") ? "is-active" : ""}
        title="Ordered List"
      >
        1.
      </button>
      <span className="tiptap-divider" />
      <button
        type="button"
        onClick={setLink}
        className={editor.isActive("link") ? "is-active" : ""}
        title="Link"
      >
        Link
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        title="Clear Formatting"
      >
        Clear
      </button>
    </div>
  );
};

function AIEnhancedRichText({ input, label, error, touched, warning, dispatch }) {
  const [isRewriting, setIsRewriting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [rewrittenContent, setRewrittenContent] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: input.value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = editor.getText().trim();
      if (text.length > 0) {
        input.onChange(html);
      } else {
        input.onChange("");
      }
    },
  });

  useEffect(() => {
    if (editor && input.value && editor.getHTML() !== input.value) {
      editor.commands.setContent(input.value);
    }
  }, [input.value, editor]);

  const handleAIRewrite = async () => {
    if (!editor) return;

    const currentText = editor.getHTML();

    if (!currentText || currentText.trim() === "" || currentText === "<p></p>") {
      dispatch(message(3000, "warning", "Voer eerst tekst in voordat je deze kunt laten herschrijven door AI"));
      return;
    }

    setIsRewriting(true);

    try {
      const response = await fetch("/api/admin/cms/ai/herschrijf-tekst", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token"),
        },
        body: JSON.stringify({
          text: currentText
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setRewrittenContent(result.rewritten);
        setShowPreview(true);
        dispatch(message(3000, "success", "Tekst succesvol herschreven met AI! Bekijk het voorstel hieronder."));
      } else {
        throw new Error(result.error || "Onbekende fout bij AI herschrijving");
      }
    } catch (err) {
      console.error("AI Rewrite Error:", err);
      dispatch(message(4000, "error", err.message || "Er is een fout opgetreden bij het herschrijven van de tekst"));
    } finally {
      setIsRewriting(false);
    }
  };

  const acceptRewrite = () => {
    if (editor) {
      editor.commands.setContent(rewrittenContent);
      input.onChange(rewrittenContent);
    }
    setShowPreview(false);
    setRewrittenContent("");
    dispatch(message(3000, "success", "AI voorstel geaccepteerd en toegepast!"));
  };

  const rejectRewrite = () => {
    setShowPreview(false);
    setRewrittenContent("");
    dispatch(message(2000, "info", "AI voorstel geweigerd. Originele tekst behouden."));
  };

  return (
    <div className="ai-enhanced-richtext">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label fw-semibold">{label}</label>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={handleAIRewrite}
          disabled={isRewriting}
          title="Herschrijf de tekst met AI op basis van de contentstijl-gids"
        >
          {isRewriting ? (
            <>
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Herschrijven...</span>
              </div>
              AI bezig...
            </>
          ) : (
            <>
              <i className="fas fa-robot me-2"></i>
              AI Herschrijven
            </>
          )}
        </button>
      </div>

      <div className="ai-enhanced-editor">
        <div className="tiptap-container">
          <MenuBar editor={editor} />
          <EditorContent editor={editor} className="tiptap-editor" />
        </div>
        {touched && ((error && <span className="text-danger mt-1 d-block">{error}</span>) ||
          (warning && <span className="text-warning mt-1 d-block">{warning}</span>))}
      </div>

      {showPreview && (
        <div className="ai-preview-container mt-3">
          <div className="card border-success">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <i className="fas fa-magic text-success me-2"></i>
                <strong className="text-success">AI Voorstel</strong>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={acceptRewrite}
                  title="Accepteer het AI voorstel"
                >
                  <i className="fas fa-check me-1"></i>
                  Accepteren
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={rejectRewrite}
                  title="Weiger het AI voorstel"
                >
                  <i className="fas fa-times me-1"></i>
                  Weigeren
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="alert alert-info mb-3">
                <small>
                  <i className="fas fa-info-circle me-1"></i>
                  Deze tekst is herschreven volgens uw AI contentstijl-gids instellingen.
                  Bekijk het resultaat en kies of u het wilt accepteren of behouden.
                </small>
              </div>
              <div className="preview-content border rounded p-3 bg-white">
                <div dangerouslySetInnerHTML={{ __html: rewrittenContent }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ai-enhanced-richtext {
          position: relative;
        }

        .ai-enhanced-richtext .btn-outline-primary {
          color: #B1BF41;
          border-color: #B1BF41;
          font-size: 0.875rem;
          padding: 0.375rem 0.75rem;
        }

        .ai-enhanced-richtext .btn-outline-primary:hover {
          background: #B1BF41;
          border-color: #B1BF41;
          color: white;
        }

        .ai-enhanced-richtext .btn-outline-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .ai-preview-container .card {
          border-radius: 0.75rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .ai-preview-container .card-header {
          border-radius: 0.75rem 0.75rem 0 0;
        }

        .preview-content {
          max-height: 300px;
          overflow-y: auto;
        }

        .spinner-border-sm {
          width: 1rem;
          height: 1rem;
        }
      `}</style>
    </div>
  );
}

export default connect()(AIEnhancedRichText);
